"use client";

import { useEffect, useRef, useState } from "react";
import type { FamousGame } from "@/content/types";

type Props = { game: FamousGame; moves: { san: string }[]; ply: number; visible: boolean };

/** All lessons stay mounted; following playback never takes over manual reading. */
export function ExplanationFeed({ game, moves, ply, visible }: Props) {
  const viewport = useRef<HTMLDivElement>(null);
  const [following, setFollowing] = useState(true);
  const moments = Object.keys(game.notes).map(Number).sort((a, b) => a - b);
  const active = moments.findLast((moment) => moment <= ply) ?? 0;

  useEffect(() => {
    if (!following || !visible) return;
    const box = viewport.current;
    const entry = box?.querySelector<HTMLElement>(`[data-moment="${active}"]`);
    if (!box || !entry) return;
    // Scroll this panel only, leaving the board and page position untouched.
    box.scrollTo({ top: box.scrollTop + entry.getBoundingClientRect().top - box.getBoundingClientRect().top - 20, behavior: "instant" });
  }, [active, following, visible]);

  return <>
    <div className="game-feed-controls">
      <span>{following ? "Following the game" : "Reading freely"}</span>
      <button type="button" aria-pressed={following} onClick={() => setFollowing(!following)}>{following ? "Follow game ✓" : "Follow game ↓"}</button>
    </div>
    <div ref={viewport} className="game-explanation-feed" role="region" aria-label="Scrollable game explanations" tabIndex={0}
      onWheel={() => setFollowing(false)} onTouchMove={() => setFollowing(false)}
      onPointerDown={() => setFollowing(false)}
      onKeyDown={(event) => { if (["ArrowUp", "ArrowDown", "PageUp", "PageDown", "Home", "End", " "].includes(event.key)) setFollowing(false); }}>
      <section data-moment="0" className="game-feed-entry" aria-label="Game introduction">
        <div className="game-move-heading"><p className="game-eyebrow">The story behind the moves</p><h2>A masterpiece, move by move</h2></div>
        <div className="game-context"><p>{game.summary}</p><p>Scroll through every key moment at your own pace. Turn on Follow game to return to the current lesson as playback continues.</p>
          <h4>Themes to watch</h4><div className="game-themes">{game.themes.map((theme) => <span key={theme}>{theme}</span>)}</div>
        </div>
      </section>
      {moments.map((moment) => {
        const explanation = game.explanations?.[moment];
        const label = `${Math.ceil(moment / 2)}${moment % 2 ? "." : "..."} ${moves[moment - 1].san}`;
        return <section key={moment} data-moment={moment} className="game-feed-entry" aria-label={label} aria-current={active === moment ? "step" : undefined}>
          <div className="game-move-heading">
            <p className="game-eyebrow">{active === moment ? "Current lesson" : moment > ply ? "Coming up" : "Earlier key moment"}</p>
            <h2>{label}</h2><h3>{explanation?.title ?? "Look a little closer"}</h3>
          </div>
          {explanation ? <>
            {explanation.sections.map((section, i) => <section className="game-lesson-section" key={section.heading}><span className="game-section-icon" aria-hidden>{["✧", "↳", "◇"][i % 3]}</span><div><h4>{section.heading}</h4><p>{section.body}</p></div></section>)}
            <section className="game-takeaway"><span aria-hidden>◇</span><div><h4>Remember</h4><p>{explanation.takeaway}</p></div></section>
          </> : <div className="game-context"><p>{game.notes[moment]}</p></div>}
        </section>;
      })}
    </div>
  </>;
}
